import { GridColumn } from '@progress/kendo-react-grid'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { createBreadcrumbWithI18n } from '~/apps/ninesegsAnalyzer/routes/paths'
import { HStack } from '~/components/elements/HStack'
import {
  PageContainer,
  type PageContainerProps,
} from '~/components/elements/PageContainer'
import { StyledGrid } from '~/components/elements/StyledGrid'
import { VStack } from '~/components/elements/VStack'
import { useLanguageTextWidth } from '~/hooks/useLanguageTextWidth'

import { useMainQueryBrandAccountUsers } from '../hooks'
import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useCurrentBrandAccountUserRole } from '../hooks/useCurrentBrandAccountUserRole'
import { useMainQueryBrandAccountSeat } from '../hooks/useMainQueryBrandAccountSeat'
import {
  canAddMForce,
  canAddPartner,
  canAddUser,
  canVisiblePartnerSeat,
} from '../utils'

import { AddButton } from './AddButton'
import styles from './BrandUserList.css'
import { CommandsCell } from './CommandsCell'
import { LoggedInAtCell } from './LoggedInAtCell'
import { RoleCell } from './RoleCell'

export const BrandUserList = () => {
  const brandAccountId = useBrandAccountId()
  const { t } = useTranslation()

  const createBreadcrumb = React.useMemo(() => createBreadcrumbWithI18n(t), [t])

  const breadcrumbs = React.useMemo<PageContainerProps['breadcrumbs']>(
    () => [
      createBreadcrumb('top', {}),
      createBreadcrumb('brandAccountSettings'),
      createBreadcrumb('brandAccountUserList', {
        brandAccountId,
      }),
    ],
    [brandAccountId, createBreadcrumb],
  )

  const currentBrandAccountUserRole =
    useCurrentBrandAccountUserRole(brandAccountId)

  const brandAccountUsers = useMainQueryBrandAccountUsers()
  const { partnerSeat, userSeat } = useMainQueryBrandAccountSeat()
  // NOTE: newlineのlint errorにひっかかるので変数宣言しています
  const partnerSeatText = `${t('partner_seats')}：${partnerSeat.used} / ${partnerSeat.total}`
  const userSeatText = `${t('user_seats')}：${userSeat.used} / ${userSeat.total}`
  const editButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 108 })
  const deleteButtonWidth = useLanguageTextWidth({ 'ja-JP': 82, 'en-US': 96 })

  // padding + button + gap + button + padding
  const commandsCellWidth = 12 + editButtonWidth + 4 + deleteButtonWidth + 12

  return (
    <PageContainer breadcrumbs={breadcrumbs}>
      <VStack spacing={2} verticalAppearance="stretch">
        <HStack alignSelf="end" spacing={2}>
          {canAddMForce(currentBrandAccountUserRole.name) && (
            <AddButton userRoleName="MFORCE_WITH_SETTING">
              {t('add_mforce')}
            </AddButton>
          )}

          {canAddPartner(currentBrandAccountUserRole.name) && (
            <AddButton userRoleName="PARTNER">{t('add_partner')}</AddButton>
          )}

          {canAddUser(currentBrandAccountUserRole.name) && (
            <AddButton userRoleName="USER">{t('add_user')}</AddButton>
          )}
        </HStack>

        <StyledGrid data={brandAccountUsers}>
          <GridColumn cell={RoleCell} title={t('role')} />

          <GridColumn field="email" title={t('email_address')} />

          <GridColumn cell={LoggedInAtCell} title={t('last_login_date')} />

          <GridColumn cell={CommandsCell} width={commandsCellWidth} />
        </StyledGrid>

        <HStack alignSelf="end" className={styles.seatNumbers} spacing={2}>
          {canVisiblePartnerSeat(currentBrandAccountUserRole.name) && (
            <div>{partnerSeatText}</div>
          )}

          <div>{userSeatText}</div>
        </HStack>
      </VStack>
    </PageContainer>
  )
}
